import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, RadialLinearScale, Title, Tooltip, Legend, Filler } from 'chart.js';
import { Line, Bar, Doughnut, Radar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, RadialLinearScale, Title, Tooltip, Legend, Filler);

const CHART_DEFAULTS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom', labels: { color: '#9CA3AF', font: { size: 12 }, boxWidth: 12, padding: 14 } },
    tooltip: { backgroundColor: 'rgba(15,15,22,0.95)', titleColor: '#E5E7EB', bodyColor: '#9CA3AF', borderColor: 'rgba(255,255,255,0.08)', borderWidth: 1 }
  },
  scales: {
    x: { grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: '#6B7280', font: { size: 11 } } },
    y: { beginAtZero: true, grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: '#6B7280', font: { size: 11 } } }
  }
};

const StatCard = ({ label, value, icon, color = '#E5E7EB', sub }) => (
  <div className="ana-stat-card">
    <div className="ana-stat-icon">{icon}</div>
    <div className="ana-stat-val" style={{ color }}>{value}</div>
    <div className="ana-stat-lbl">{label}</div>
    {sub && <div className="ana-stat-sub">{sub}</div>}
  </div>
);

// apiBase/token: yonetim panelindeki AI Center bu bilesenleri DEGISTIRMEDEN
// yeniden kullanabilsin diye opsiyonel - verilmezse tenant'taki mevcut
// davranis (kendi crm_token'i + /api/callops) AYNEN korunur.
const AnalyticsTab = ({ apiBase = '/api/callops', token } = {}) => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const authToken = token || localStorage.getItem('crm_token');

  useEffect(() => { fetchAnalytics(); }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/analytics?period=${period}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      setAnalyticsData(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const data = analyticsData || {};
  const monthlyTrend = data.monthlyTrend || [];
  const channelData = data.channelPerformance || [];
  const agentPerformance = data.agentPerformance || [];
  const intentData = data.intentDistribution || [];

  const monthlyChartData = {
    labels: monthlyTrend.map(m => m.month),
    datasets: [
      { label: 'Aramalar', data: monthlyTrend.map(m => m.calls), borderColor: '#60A5FA', backgroundColor: 'rgba(96,165,250,0.08)', fill: true, tension: 0.4, borderWidth: 2 },
      { label: 'Konuşmalar', data: monthlyTrend.map(m => m.conversations), borderColor: '#34D399', backgroundColor: 'rgba(52,211,153,0.08)', fill: true, tension: 0.4, borderWidth: 2 }
    ]
  };

  const channelChartData = {
    labels: channelData.map(c => c.channel),
    datasets: [{
      label: 'Kanal Performansı',
      data: channelData.map(c => c.count),
      backgroundColor: ['rgba(37,211,102,0.7)', 'rgba(228,64,95,0.7)', 'rgba(99,102,241,0.7)', 'rgba(251,191,36,0.7)'],
      borderRadius: 6, borderWidth: 0
    }]
  };

  const radarData = {
    labels: agentPerformance.map(a => a.name),
    datasets: [{
      label: 'Agent Performansı',
      data: agentPerformance.map(a => a.score),
      backgroundColor: 'rgba(96,165,250,0.12)',
      borderColor: '#60A5FA',
      pointBackgroundColor: '#60A5FA',
      borderWidth: 2,
      pointRadius: 4
    }]
  };

  const intentChartData = {
    labels: intentData.map(i => i.intent),
    datasets: [{
      data: intentData.map(i => i.count),
      backgroundColor: ['rgba(96,165,250,0.7)', 'rgba(52,211,153,0.7)', 'rgba(251,191,36,0.7)', 'rgba(248,113,113,0.7)', 'rgba(167,139,250,0.7)', 'rgba(251,146,60,0.7)', 'rgba(244,114,182,0.7)'],
      borderWidth: 0, hoverOffset: 8
    }]
  };

  return (
    <div className="analytics-tab">
      {/* Header */}
      <div className="ana-header">
        <div>
          <h2>📈 Analitik</h2>
          <p style={{ margin:0, color:'#6B7280', fontSize:13 }}>AI performans metrikleri</p>
        </div>
        <div className="period-selector">
          {[['7d','7g'], ['30d','30g'], ['90d','90g'], ['1y','1y']].map(([v,l]) => (
            <button key={v} className={period === v ? 'active' : ''} onClick={() => setPeriod(v)}>{l}</button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="ana-stats-grid">
        <StatCard icon="⚡" label="Toplam Etkileşim" value={data.totalInteractions?.toLocaleString() || '0'} color="#60A5FA" />
        <StatCard icon="⏱️" label="Ort. Yanıt Süresi" value={data.avgResponseTime || '0s'} color="#34D399" />
        <StatCard icon="🎯" label="AI Başarı Oranı" value={`${data.aiSuccessRate?.toFixed(1) || 0}%`} color="#a78bfa" />
        <StatCard icon="💰" label="Toplam Maliyet" value={`$${data.totalCost?.toFixed(2) || '0.00'}`} color="#FBBF24" />
      </div>

      {loading ? (
        <div className="ana-loading"><div className="ana-spinner" /><span>Analitik veriler yükleniyor...</span></div>
      ) : (
        <>
          {/* Charts Row 1 */}
          <div className="ana-charts-row" style={{ gridTemplateColumns: '2fr 1fr' }}>
            <div className="ana-chart-card">
              <h3>📅 Aylık Trend</h3>
              <div className="ana-chart-wrap"><Line data={monthlyChartData} options={CHART_DEFAULTS} /></div>
            </div>
            <div className="ana-chart-card">
              <h3>📡 Kanal Karşılaştırma</h3>
              <div className="ana-chart-wrap">
                <Bar data={channelChartData} options={{ ...CHART_DEFAULTS, plugins: { ...CHART_DEFAULTS.plugins, legend: { display: false } } }} />
              </div>
            </div>
          </div>

          {/* Charts Row 2 */}
          <div className="ana-charts-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="ana-chart-card">
              <h3>🎯 Intent Dağılımı</h3>
              <div className="ana-chart-wrap doughnut">
                <Doughnut data={intentChartData} options={{
                  ...CHART_DEFAULTS, scales: undefined,
                  plugins: { ...CHART_DEFAULTS.plugins }
                }} />
              </div>
            </div>
            <div className="ana-chart-card">
              <h3>🤖 Agent Radar</h3>
              <div className="ana-chart-wrap">
                <Radar key={`radar-${period}`} data={radarData} options={{
                  responsive: true, maintainAspectRatio: false,
                  plugins: { legend: { display: false }, tooltip: CHART_DEFAULTS.plugins.tooltip },
                  scales: { r: { beginAtZero: true, max: 100, grid: { color: 'rgba(255,255,255,0.06)' }, ticks: { color: '#6B7280', backdropColor: 'transparent', font: { size: 10 } }, pointLabels: { color: '#9CA3AF', font: { size: 11 } } } }
                }} />
              </div>
            </div>
          </div>

          {/* Intent Table */}
          {intentData.length > 0 && (
            <div className="ana-table-card">
              <h3>📊 Intent Detay Tablosu</h3>
              <table className="ana-table">
                <thead>
                  <tr><th>#</th><th>Intent</th><th>Sayı</th><th>Oran</th><th>Bar</th></tr>
                </thead>
                <tbody>
                  {intentData.map((item, i) => {
                    const pct = ((item.count / (data.totalInteractions || 1)) * 100).toFixed(1);
                    return (
                      <tr key={i} className="ana-row">
                        <td className="ana-idx">{i + 1}</td>
                        <td className="ana-intent">{item.intent}</td>
                        <td className="ana-count">{item.count}</td>
                        <td className="ana-pct">{pct}%</td>
                        <td className="ana-bar-cell">
                          <div className="ana-bar-track">
                            <div className="ana-bar-fill" style={{ width: `${pct}%` }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <style>{`
        .analytics-tab { display:flex; flex-direction:column; gap:24px; }

        .ana-header { display:flex; justify-content:space-between; align-items:flex-start; }
        .ana-header h2 { margin:0 0 2px; font-size:22px; font-weight:700; color:#F9FAFB; }

        .period-selector { display:flex; gap:4px; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07); padding:4px; border-radius:12px; }
        .period-selector button { padding:8px 16px; border:none; background:transparent; color:#6B7280; font-weight:500; font-size:13px; cursor:pointer; border-radius:8px; transition:all 0.2s; }
        .period-selector button.active { background:linear-gradient(135deg,#1d4ed8,#60A5FA); color:#fff; }

        .ana-stats-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; }
        .ana-stat-card { background:rgba(17,17,24,0.7); border:1px solid rgba(255,255,255,0.07); border-radius:16px; padding:20px; text-align:center; backdrop-filter:blur(16px); transition:all 0.2s; }
        .ana-stat-card:hover { transform:translateY(-2px); border-color:rgba(255,255,255,0.12); }
        .ana-stat-icon { font-size:24px; margin-bottom:8px; }
        .ana-stat-val { font-size:28px; font-weight:800; margin-bottom:4px; line-height:1; }
        .ana-stat-lbl { font-size:12px; color:#6B7280; text-transform:uppercase; letter-spacing:0.04em; }
        .ana-stat-sub { font-size:11px; color:#4B5563; margin-top:3px; }

        .ana-loading { display:flex; align-items:center; justify-content:center; flex-direction:column; gap:14px; padding:80px; color:#6B7280; font-size:14px; }
        .ana-spinner { width:36px; height:36px; border:3px solid rgba(255,255,255,0.08); border-top-color:#60A5FA; border-radius:50%; animation:spin 0.8s linear infinite; }
        @keyframes spin { to { transform:rotate(360deg); } }

        .ana-charts-row { display:grid; gap:18px; }
        .ana-chart-card { background:rgba(17,17,24,0.7); border:1px solid rgba(255,255,255,0.07); border-radius:18px; padding:24px; backdrop-filter:blur(16px); }
        .ana-chart-card h3 { margin:0 0 20px; font-size:15px; font-weight:600; color:#E5E7EB; }
        .ana-chart-wrap { height:260px; }
        .ana-chart-wrap.doughnut { display:flex; align-items:center; justify-content:center; }

        .ana-table-card { background:rgba(17,17,24,0.7); border:1px solid rgba(255,255,255,0.07); border-radius:18px; padding:24px; backdrop-filter:blur(16px); }
        .ana-table-card h3 { margin:0 0 20px; font-size:15px; font-weight:600; color:#E5E7EB; }
        .ana-table { width:100%; border-collapse:collapse; }
        .ana-table thead th { padding:10px 14px; text-align:left; font-size:11px; text-transform:uppercase; letter-spacing:0.06em; color:#6B7280; font-weight:700; border-bottom:1px solid rgba(255,255,255,0.06); }
        .ana-row { transition:background 0.15s; }
        .ana-row:hover { background:rgba(255,255,255,0.03); }
        .ana-row td { padding:11px 14px; border-bottom:1px solid rgba(255,255,255,0.04); vertical-align:middle; }
        .ana-row:last-child td { border-bottom:none; }
        .ana-idx { font-size:12px; color:#4B5563; width:24px; }
        .ana-intent { font-size:14px; color:#E5E7EB; font-weight:500; }
        .ana-count { font-size:14px; font-weight:700; color:#E5E7EB; }
        .ana-pct { font-size:13px; color:#60A5FA; font-weight:600; width:50px; }
        .ana-bar-cell { width:160px; }
        .ana-bar-track { background:rgba(255,255,255,0.07); border-radius:4px; height:6px; overflow:hidden; }
        .ana-bar-fill { height:100%; border-radius:4px; background:linear-gradient(90deg,#1d4ed8,#60A5FA); transition:width 0.4s; }

        @media (max-width:900px) {
          .ana-stats-grid { grid-template-columns:repeat(2,1fr); }
          .ana-charts-row { grid-template-columns:1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default AnalyticsTab;
